/**
 * CustomerLogin.jsx
 * Elegant, calming login page for wedding customers
 * Design: Warm, reassuring, wedding-friendly
 */

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Heart, Mail, Lock, Eye, EyeOff, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/customer`;

const CustomerLogin = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [content, setContent] = useState({});

  // Fetch CMS content
  useEffect(() => {
    const fetchContent = async () => {
      try {
        const response = await axios.get(`${API}/dashboard/content`);
        setContent(response.data);
      } catch (error) {
        console.error("Failed to fetch content:", error);
      }
    };
    fetchContent();
  }, []);

  // Check if already logged in
  useEffect(() => {
    const token = localStorage.getItem("customer_token");
    if (token) {
      navigate("/my-wedding/dashboard");
    }
  }, [navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/login`, {
        email: email.trim().toLowerCase(),
        password
      });

      if (response.data.success) {
        // Store token and customer data
        localStorage.setItem("customer_token", response.data.token);
        localStorage.setItem("customer_data", JSON.stringify(response.data.customer));
        localStorage.setItem("wedding_data", JSON.stringify(response.data.wedding));
        
        toast.success("Welcome back! Let's plan your celebration.");
        navigate("/my-wedding/dashboard");
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Unable to sign in. Please check your credentials.";
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FDF8F5] via-[#FAF5F2] to-[#F5EDE8] flex items-center justify-center p-4">
      {/* Background decoration */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#C9A87C]/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#D4B896]/10 rounded-full blur-3xl" />
      </div>

      <div className="w-full max-w-md relative z-10">
        {/* Logo / Brand */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-[#C9A87C] to-[#B08968] mb-4 shadow-lg">
            <Heart className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl sm:text-3xl font-serif text-[#5C4A3D] mb-2">
            {content.login_title || "Access Your Wedding Dashboard"}
          </h1>
          <p className="text-[#8B7355] text-sm sm:text-base">
            {content.login_subtitle || "Your personal space to plan, prepare, and celebrate"}
          </p>
        </div>

        {/* Login Card */}
        <div className="bg-white/80 backdrop-blur-md rounded-3xl shadow-xl border border-[#E8DED5] p-6 sm:p-8">
          <form onSubmit={handleLogin} className="space-y-5">
            {/* Email Field */}
            <div className="space-y-2">
              <Label htmlFor="email" className="text-[#5C4A3D] font-medium">
                Email Address
              </Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#B08968]" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="pl-10 h-12 bg-[#FAF5F2] border-[#E8DED5] focus:border-[#C9A87C] focus:ring-[#C9A87C]/20 rounded-xl"
                  required
                  data-testid="customer-login-email"
                />
              </div>
            </div>

            {/* Password Field */}
            <div className="space-y-2">
              <Label htmlFor="password" className="text-[#5C4A3D] font-medium">
                Password
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#B08968]" />
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="pl-10 pr-10 h-12 bg-[#FAF5F2] border-[#E8DED5] focus:border-[#C9A87C] focus:ring-[#C9A87C]/20 rounded-xl"
                  required
                  data-testid="customer-login-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8B7355] hover:text-[#5C4A3D]"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-gradient-to-r from-[#C9A87C] to-[#B08968] hover:from-[#B8976B] hover:to-[#9F7857] text-white rounded-xl font-medium text-base shadow-md transition-all duration-200"
              data-testid="customer-login-submit"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                "Enter My Wedding Dashboard"
              )}
            </Button>
          </form>

          {/* Footer Note */}
          <div className="mt-6 pt-6 border-t border-[#E8DED5]">
            <p className="text-center text-sm text-[#8B7355]">
              {content.login_footer || "Credentials provided by Happy Retreats team"}
            </p>
          </div>
        </div>

        {/* Help Section */}
        <div className="mt-6 text-center">
          <p className="text-sm text-[#8B7355]">
            Need help accessing your account?
          </p>
          <a 
            href="tel:+919876543210" 
            className="inline-flex items-center gap-1 mt-1 text-[#B08968] hover:text-[#8B6914] font-medium text-sm"
          >
            Call us at +91 98765 43210
          </a>
        </div>

        {/* Brand Footer */}
        <div className="mt-8 text-center">
          <p className="text-xs text-[#B8A89A]">
            A Happy Retreats Experience
          </p>
        </div>
      </div>
    </div>
  );
};

export default CustomerLogin;
